import { Button, DestructiveConfirmDialog, TimelineInteractiveCard, useUncontrolledState } from "@tellescope/ui";
import { TimelineCardBaseProps } from "../types";
import { Copy, Eye, Mail, Pin, Send, Tag, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useAuth,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { useState } from "react";
import { EmailContextCard } from "@tellescope/messaging";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AddTagContent } from "../AddTagContent";
import { formatEmailTitle } from "@tellescope/utils";

export interface EmailTimelineCardProps extends TimelineCardBaseProps<"emails"> {}

export const EmailTimelineCard = ({ item: email, className, open, onOpenChange }: EmailTimelineCardProps) => {
  const { t } = useTranslation("timeline");
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useUncontrolledState<boolean>({
    state: open ?? false,
    setState: onOpenChange && ((next) => onOpenChange(next instanceof Function ? next(open ?? false) : next)),
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    delete: { access: canDelete },
    create: { access: canReply },
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "emails",
    actions: ["delete", "create", "update"],
  });

  const deleteEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "deleteOne",
  });

  const updateEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "updateOne",
  });

  const openEmail = () => {
    setIsOpen(true);
    if (currentUser) {
      updateEmailMutation.mutateAsync({
        id: email.id,
        readBy: {
          [currentUser.id]: new Date(),
        },
      });
    }
  };

  const threadIdQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      limit: 1000,
      mdbFilter: {
        enduserIds: email.enduserId ? [email.enduserId] : [],
        type: "Email",
      },
    },
    getNextPageParam: (lastPage) => lastPage.threads[lastPage.threads.length - 1]?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) =>
      data.pages
        .flatMap((page) => page.threads)
        .find((thread) => formatEmailTitle(thread.title) === formatEmailTitle(email.subject))?.id,
  });

  return (
    <>
      <TimelineInteractiveCard
        icon={Mail}
        title={formatEmailTitle(email.subject) || t("No Subject")}
        timestamp={email.timestamp ?? email.createdAt}
        isPinned={!!email.pinnedAt}
        isUnread={currentUser ? !email.readBy?.[currentUser.id] : false}
        preview={{
          content: email.textContent || "",
        }}
        disablePreview
        onHeaderClick={isOpen ? undefined : openEmail}
        tags={email.tags}
        primaryAction={
          isOpen
            ? [
                {
                  label: t("Reply"),
                  content:
                    threadIdQuery.data && canReply ? (
                      <Link to={`/messaging/inbox/email/${threadIdQuery.data}`}>
                        <Button size="sm" className="max-h-[30px]">
                          <Send />
                          {t("Reply")}
                        </Button>
                      </Link>
                    ) : (
                      <div />
                    ),
                },
                {
                  label: t("Collapse"),
                  onClick: () => {
                    setIsOpen(false);
                  },
                },
              ]
            : undefined
        }
        actions={[
          [
            {
              icon: Eye,
              label: t("View Email"),
              onClick: openEmail,
            },
            {
              icon: Trash2,
              label: t("Delete Email"),
              onClick: () => {
                setIsDeleting(true);
              },
              hidden: !canDelete,
            },
          ],
          [
            {
              icon: Copy,
              label: t("Copy ID"),
              onClick: async () => {
                await navigator.clipboard.writeText(email.id);
                toast(t("Email ID copied"), {
                  description: t("{{emailId}} has been copied to clipboard", { emailId: email.id }),
                });
              },
            },
          ],
          [
            {
              icon: Tag,
              label: t("Add Tag"),
              submenu: (
                <AddTagContent
                  currentTags={email.tags ?? []}
                  onTagClick={async (tag) => {
                    if (!email.tags?.includes(tag)) {
                      await updateEmailMutation.mutateAsync(
                        {
                          id: email.id,
                          tags: [...(email.tags ?? []), tag],
                        },
                        {
                          onError: () => {
                            toast.error(t("Error tagging. Please try again."));
                          },
                        }
                      );
                    } else {
                      await updateEmailMutation.mutateAsync(
                        {
                          id: email.id,
                          tags: email.tags?.filter((name) => name !== tag) ?? [],
                          _options: {
                            replaceObjectFields: true,
                          },
                        },
                        {
                          onError: () => {
                            toast.error(t("Error removing tag. Please try again."));
                          },
                        }
                      );
                    }
                  }}
                  model="emails"
                />
              ),
              hidden: !canUpdate,
            },
            {
              icon: Pin,
              fillIcon: !!email.pinnedAt,
              label: email.pinnedAt ? t("Remove Pin") : t("Pin"),
              onClick: () => {
                updateEmailMutation.mutateAsync({
                  id: email.id,
                  pinnedAt: email.pinnedAt ? "" : new Date(),
                });
              },
              hidden: !canUpdate,
            },
          ],
        ]}
        className={className}
      >
        {!!isOpen && <EmailContextCard emailId={email.id} />}
      </TimelineInteractiveCard>
      <DestructiveConfirmDialog
        onConfirm={() => {
          deleteEmailMutation.mutateAsync(email.id);
        }}
        onCancel={() => setIsDeleting(false)}
        onOpenChange={setIsDeleting}
        open={isDeleting}
        description={t("This will permanently delete the email. This action cannot be undone.")}
      />
    </>
  );
};
